import { CommerceSalesOrder } from '@/models/api/commerce-sales-order'
import { ApiCoupon } from '@/models/api/coupon'
import {
  ErrorResponse,
  OrderPurchaseFail as PurchaseFail,
  OrderPurchaseResult as PurchaseResult,
  OrderPurchaseSuccess as PurchaseSuccess,
} from '../../../services/order'

import { SelectOptionList } from '@/models/api/payment/parameter/type/user-select'
import { Payment as ApiPayment } from '@/models/api/payment/payment'
import { PaymentType } from '@/models/app-config/behavior/types'
import { DateWithoutHour } from '@/models/date-time/date-without-hour'
import { DeliveryMethodDetail, DeliveryTime, OkihaiPlace } from '@/models/delivery'
import { ResolverResource } from '@/services/payment/variable-resolver'

/**
 * @module store/modules/orderConfirm/types
 */

export const initialOrderConfirmState: OrderConfirmState = {
  cartIds: null,
  payment: null,
  delivery: null,
  deliveryDate: null,
  coupon: {
    selected: null,
  },
  point: {
    applied: 0,
    discounted: 0,
  },
  complete: null,
  selectedDeliveryConfig: null,
}

export interface Delivery {
  deliveryType: string
  id: string
  deliveryMethod: string
  /**
   * 置き配場所を示すID
   *
   * 置き配時のみ指定
   */
  unattendedDelivery: string | null
}

export interface DeliveryDate {
  deliveryPlanDate: string
  deliveryTimeSlot: string
}

export interface DeliveryConfig {
  deliveryMethodDetail: DeliveryMethodDetail
  deliveryDate: DateWithoutHour | null
  deliveryTime: DeliveryTime
  okihaiPlace: OkihaiPlace | null
}

/**
 * 見積もり・決済時のポイント利用パラメータ
 * @see https://my-color.esa.io/posts/559
 */
export interface Point {
  kind: 'point',
  amount: number,
}

/**
 * TODO: Separate each type definition for each paymentType
 * TODO: @/services/payment/types のPaymentを利用するよう置換していく
 *
 * lastCreditDateは注文確定リクエストまでの中間データとしてのみ必要だったり、
 * 逆にpaymentid, auth_tokenなどは注文確定リクエストのpayloadとしてのみ必要だったりするので
 * 最終的には中間データと注文確定リクエストのpayloadとで分けるのが良さそう
 *
 * そもそも、このデータ型が何を意味するものなのか、不明瞭？
 * store管理のためのデータ構造なのか、APIパラメータのデータ構造なのか.
 * 前者と捉えるには最適化されていないし、後者と捉えるにはoptional含めノイズが多い.
 * 実質、APIパラメータ用データを直接保持しつつ、計算に便利な値をついでに生やしている、という格好になっているか.
 *
 * アプリケーション管理の範囲と外部サービス依存の範囲が暗黙的に混濁し結合した状態になっているので、
 * 両者をそれぞれ分離した上で、透過的に使得る形にした方が良いか、両者を完全に分断するか検討した方が良さそうか.
 * {@link https://github.com/my-color/front/pull/6787#discussion_r932773438} も参照.
 */
export interface Payment {
  payment_type: PaymentType,
  point?: Point,

  token?: string, // creditcard
  numberLast4?: string // creditcard
  cardType?: string // creditcard
  lastCreditDate?: string // creditcard
  paymentid?: string, // atone | paidy
  auth_token?: string, // atone
  auth_pass_full_name?: string, // creditcard(kuroneko)
  auth_pass_tel?: string, // creditcard(kuroneko)
  auth_pass_email?: string, // creditcard(kuroneko)
  /**
   * クレジットカードの場合 & サブスクリプションの場合のみ、
   * Engine都合で app_kind というキーを指定する必要がある
   * @see https://github.com/my-color/front/issues/5636
   * @see https://my-color.esa.io/posts/707
   */
  app_kind?: string // creditcard

  /**
   * 利用可能な支払い方法一覧取得APIの返却する支払い方法オブジェクト
   *
   * 通常の購入時には必ずセットされるようになっているはずだが、
   * サブスク（有料会員）申込時にもこのPayment型が利用されているのでoptionalとしている。
   */
  apiPayment?: ApiPayment,
  selectedOptionList?: SelectOptionList,
  variableResource?: ResolverResource,
}

/**
 * TODO: Paymentのユースケースを整理して、apiPaymentが必ず入る / 必ず入らないのケースで型を分ける
 * また、apiPaymentが必ず入る場合、以下の整理も同時に行う
 * - payment_typeはapiPayment.idと重複するので統合する
 * - paymentidやauth_*などはvariableResourceに含まれるので統合する
 * - 情報がAPIから取得されたものであることを意識する必要はないので"api"Paymentという名前を変更する
 */
export type PaymentWithApiPayment = Payment & { apiPayment: ApiPayment }
export const isPaymentWithApiPayment =
  (payment: Payment): payment is PaymentWithApiPayment => !!payment.apiPayment

// FIXME: Until common 'Payment' is moved away with refactoring, make sure this type within 'Payment' keys.
export interface SimplePayment {
  payment_type: PaymentType
  point?: Point,
}

/**
 * TODO: Separate interface of 'kuroneko' and other creditcard types.
 * FIXME: Until common 'Payment' is moved away with refactoring, make sure this type within 'Payment' keys.
 * FIXME: Payment と定義が二重化している? ↑がstore用で↓がパラメータ? 役割分担が不明. 影響範囲が大きくなるのでついでの統合は難しそう.
 * クレジットカード用パラメータが増えるたびに二箇所に定義追加するのは無駄なので、一元化したい & するべき
 *
 * @see mutations.setPaymentToCreditcard
 */
export interface CreditcardPayment {
  payment_type: 'creditcard'
  token: string,
  numberLast4: string,
  cardType: string,
  lastCreditDate?: string
  /**
   * クロネコによるカード決済専用
   * @see https://github.com/my-color/front/issues/1347
   */
  auth_pass_full_name?: string,
  /**
   * クロネコによるカード決済専用
   * @see https://github.com/my-color/front/issues/1347
   */
  auth_pass_tel?: string,
  /**
   * クロネコによるカード決済専用
   * @see https://github.com/my-color/front/issues/1347
   */
  auth_pass_email?: string,
  /**
   * 定期購入でクレジットカードを利用する際、Engine都合により
   * 「クレジットカードで決済したとき、そのカードをどの購入方法で利用したのか」を区別する必要がある.
   * そのため、 frontから app_kind として その利用方法を明示することが要求されている.
   *
   * @see https://my-color.esa.io/posts/707
   */
  app_kind?: string,
  point?: Point,
}

export const isCreditCard = (payment: Pick<Payment, 'payment_type'>): payment is CreditcardPayment =>
  /**
   * 2023/12/07時点で、{@see module:store/modules/orderConfirm/types.Payment}は
   * 「Front側の状態を表す型」と「APIパラメータを表す型」の両方の役割を持たされている.
   * そのために、Frontの都合で安易にパラメータを増やしにくい.
   *
   * 本来なら{@see module:models/api/payment.Payment.isCreditCard}と同様に分類（kind）を持たせて同様の判定にしたいが、
   * 上記の理由でこの箇所だけ異なる判定方法を撮ってしまっている.
   *
   * 「Front側の状態を表す型」と「APIパラメータを表す型」とを区別して、
   * それぞれの役割を明確にすることで、このような判定の散逸をなくしたい.
   */
  typeIsCreditCard(payment.payment_type)

export const typeIsCreditCard = (paymentType: string): paymentType is 'creditcard' | 'creditcard3d' =>
  paymentType === 'creditcard' || paymentType === 'creditcard3d'

// FIXME: Until common 'Payment' is moved away with refactoring, make sure this type within 'Payment' keys.
export interface AtonePayment {
  payment_type: 'atone'
  paymentid?: string,
  auth_token?: string
  point?: Point,
  apiPayment: ApiPayment,
}

export const isAtone = (payment: Payment): payment is AtonePayment => {
  return payment.payment_type === 'atone'
}

// FIXME: Until common 'Payment' is moved away with refactoring, make sure this type within 'Payment' keys.
export interface PaidyPayment {
  payment_type: 'paidy'
  paymentid?: string,
  point?: Point,
  apiPayment: ApiPayment,
}

export const isPaidy = (payment: Payment): payment is PaidyPayment => {
  return payment.payment_type === 'paidy'
}

export interface Complete {
  orderResults: OrderResult[]
  bundleDiscountPrice: number
  couponDiscountPrice: number
  isFirstShareItem: boolean
  isFreeShipping: boolean
  canScheduleDelivery: boolean
}
export interface OrderResult extends PurchaseResult {
  commerceSalesOrder: CommerceSalesOrder | null
}

export interface OrderResultSuccess extends OrderResult, PurchaseSuccess  {
  commerceSalesOrder: CommerceSalesOrder
  success: true
  error: null
}

export interface OrderResultFail extends OrderResult, PurchaseFail {
  commerceSalesOrder: null
  success: false
  error: ErrorResponse
  redirectHtml: undefined
}

export interface OrderConfirmState {
  cartIds: string[] | null,
  payment: Payment | null,
  delivery: Delivery | null,
  deliveryDate: DeliveryDate | null,
  coupon: SelectedCoupon,
  point: AppliedPoint,
  complete: Complete | null,
  /**
   * ユーザーが最後に選択した配送設定.
   *
   * SPA画面遷移の過程で「一度選択した配送方法Aが、何らかの理由で利用不可になったが、
   * さらなる操作の結果またAが使用可能になった」という状況で、
   * 改めてAが選択された状態にするために保持されている.
   *
   * 配送方法Aが利用不可になった場合でも、再び利用可能となったときのためにこのプロパティはAを保持し続ける.
   * そのため、このプロパティの値は実際に注文や見積もりを行う時に使って良いものとは限らない.
   *
   * 注文や見積もりに使う配送情報は delivery プロパティから取得すること.
   */
  selectedDeliveryConfig: DeliveryConfig | null,
}

export interface AppliedPoint {
  applied: number
  discounted: number
}

/**
 * ユーザーのクーポン選択状態.
 * 適用状態とは異なることに注意
 * @see https://my-color.esa.io/posts/1125
 */
export interface SelectedCoupon {
  selected: ApiCoupon | null
}
